/**
 * Host side of the morph entries (docs/concepts/chat-ux-handoff, 01 section
 * 4.1-4.5, 03 sections 5.1/5.2, 6, 12; 05 section 2).
 *
 * The chat surface itself morphs inside the iframe. The host only:
 * - measures the origin (rect, radius, skin, text/icon snapshot) before any
 *   style change,
 * - plays the 60 ms anticipation on the origin,
 * - hides the origin once the iframe reports `morph-started` (never earlier:
 *   one frame of overlap with identical skin is invisible, one frame of gap is
 *   not), shows it again on `morph-landed` with the landing bounce,
 * - keeps the iframe informed when the origin moves while the chat is open,
 * - types example questions into chat-button blocks.
 *
 * Everything here only runs for triggers with `data-dhsv-chat-morph`.
 */
import type {
	EntrySource,
	HostBox,
	HostOriginSkin,
	HostOriginSnapshot,
	HostRect,
	MorphMotionFlags,
	MorphPlacement,
} from './iframe-protocol';
import { getMotionDebugFlags, type MotionDebugFlags } from './motion-debug';
import {
	createTypingLoop,
	parseExamplesAttribute,
	type TypingLoop,
} from './typing-engine';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const MORPH_ATTRIBUTE = 'data-dhsv-chat-morph';
export const ENTRY_ATTRIBUTE = 'data-dhsv-chat-entry';
export const PLACEMENT_ATTRIBUTE = 'data-dhsv-chat-placement';
export const EXAMPLES_ATTRIBUTE = 'data-dhsv-chat-examples';

/** Class the chat-button block hides itself with while the surface is out. */
export const ORIGIN_OPEN_CLASS = 'is-open';
/** Class that stops the caret blinking while characters move. */
export const TYPING_CLASS = 'is-typing';

const TEXT_SELECTOR = '.dhsv-chat-button__text, [data-dhsv-chat-morph-text]';
const ICON_SELECTOR = '.dhsv-chat-button__icon, [data-dhsv-chat-morph-icon]';

/** 03 section 6.1: compress to 97 % in 60 ms. */
export const ANTICIPATION_MS = 60;
export const ANTICIPATION_SCALE = 0.97;
const ANTICIPATION_EASE = 'cubic-bezier(.3,0,.4,1)';
/** 03 section 6.1: release back to 100 % when nothing takes over. */
export const RELEASE_MS = 340;
const RELEASE_EASE = 'cubic-bezier(.2,.9,.3,1.25)';
/** 03 section 5.2: landing bounce on the origin. */
export const LANDING_BOUNCE_MS = 520;
const LANDING_BOUNCE_EASE = 'cubic-bezier(.3,.7,.4,1)';
const LANDING_BOUNCE_KEYFRAMES: Keyframe[] = [
	{ scale: '1' },
	{ scale: '1.02 1.028', offset: 0.35 },
	{ scale: '.996 .994', offset: 0.7 },
	{ scale: '1' },
];
/**
 * The iframe should answer `morph-started` within two frames. Past this the
 * held anticipation is released so the origin never stays compressed.
 */
export const MORPH_START_TIMEOUT_MS = 1000;
/**
 * A morph close shrinks the iframe on `morph-landed` (or on `drawer-close`
 * after it); this is the safety net when the landing never arrives.
 */
export const MORPH_LAND_TIMEOUT_MS = 2000;
/**
 * Cold start (01 section 5): a trigger that waits for the iframe shows a
 * loading state only after this long, so a fast load never flashes it.
 */
export const COLD_START_LOADING_DELAY_MS = 300;
/** Class of a morph trigger that waits for the chat past that delay. */
export const LOADING_CLASS = 'is-loading';

/** Fallback skin for a transparent origin (prototype `glassOf`). */
const TRANSPARENT_FALLBACK_BG = 'rgba(255,255,255,.12)';

const ENTRY_SOURCES: readonly EntrySource[] = [
	'nav',
	'stage',
	'inline',
	'bubble',
	'speech',
	'pill',
	'anfrage',
	'api',
];

// ---------------------------------------------------------------------------
// Attributes
// ---------------------------------------------------------------------------

export function isMorphTrigger( element: Element | null ): boolean {
	return !! element && element.hasAttribute( MORPH_ATTRIBUTE );
}

export function readPlacement( element: Element ): MorphPlacement | undefined {
	const value = element.getAttribute( PLACEMENT_ATTRIBUTE );
	return value === 'center' || value === 'corner' || value === 'sheet'
		? value
		: undefined;
}

export function readEntrySource( element: Element ): EntrySource | undefined {
	const value = element.getAttribute( ENTRY_ATTRIBUTE ) as EntrySource;
	return ENTRY_SOURCES.includes( value ) ? value : undefined;
}

// ---------------------------------------------------------------------------
// Motion flags
// ---------------------------------------------------------------------------

export interface MotionEnvironment {
	debug: MotionDebugFlags;
	reducedMotion: boolean;
	reducedTransparency: boolean;
	/** `html.is-lite` (a theme's frame-rate governor). */
	liteClass: boolean;
}

/**
 * Flags handed to the iframe. Pure.
 * @param env
 */
export function resolveMorphMotionFlags(
	env: MotionEnvironment
): MorphMotionFlags {
	return {
		reducedMotion: env.reducedMotion,
		lite: env.debug.lite || env.reducedTransparency || env.liteClass,
		snap: env.debug.snap && ! env.reducedMotion,
		slow: env.debug.slow,
	};
}

/**
 * Whether the host plays the anticipation itself (03 section 6).
 * @param flags
 */
export function shouldAnticipate( flags: MorphMotionFlags ): boolean {
	return flags.snap && ! flags.lite && ! flags.reducedMotion;
}

function matches( query: string ): boolean {
	try {
		return !! window.matchMedia?.( query ).matches;
	} catch {
		return false;
	}
}

export function readMotionEnvironment(): MotionEnvironment {
	return {
		debug: getMotionDebugFlags(),
		reducedMotion: matches( '(prefers-reduced-motion: reduce)' ),
		reducedTransparency: matches(
			'(prefers-reduced-transparency: reduce)'
		),
		liteClass: document.documentElement.classList.contains( 'is-lite' ),
	};
}

// ---------------------------------------------------------------------------
// Measuring
// ---------------------------------------------------------------------------

/**
 * Effective corner radius: the computed top-left radius (px or %), clamped to
 * half the shorter side; an unparsable value means a full pill. Pure.
 * @param raw    Computed `border-top-left-radius`.
 * @param width
 * @param height
 */
export function clampRadius(
	raw: string | null | undefined,
	width: number,
	height: number
): number {
	const max = Math.max( 0, Math.min( width, height ) / 2 );
	const first = ( raw || '' ).trim().split( /\s+/ )[ 0 ] ?? '';
	const n = parseFloat( first );
	if ( ! Number.isFinite( n ) ) {
		return max;
	}
	const px = first.endsWith( '%' )
		? ( n / 100 ) * Math.min( width, height )
		: n;
	return Math.max( 0, Math.min( px, max ) );
}

/**
 * Blur radius and saturation of a computed `backdrop-filter`. Pure.
 * @param value
 */
export function parseBackdropFilter( value: string | null | undefined ): {
	blurPx: number;
	saturate?: number;
} {
	const text = value || '';
	const blur = /blur\(\s*([\d.]+)px\s*\)/.exec( text );
	const sat = /saturate\(\s*([\d.]+)(%?)\s*\)/.exec( text );
	const out: { blurPx: number; saturate?: number } = {
		blurPx: blur ? parseFloat( blur[ 1 ] as string ) : 0,
	};
	if ( sat ) {
		const n = parseFloat( sat[ 1 ] as string );
		out.saturate = sat[ 2 ] === '%' ? n / 100 : n;
	}
	return out;
}

function isTransparent( color: string ): boolean {
	const c = color.replace( /\s+/g, '' ).toLowerCase();
	return (
		c === '' ||
		c === 'transparent' ||
		/^rgba\(\d+,\d+,\d+,0(\.0+)?\)$/.test( c )
	);
}

function box( rect: DOMRect ): HostBox {
	return {
		left: rect.left,
		top: rect.top,
		width: rect.width,
		height: rect.height,
	};
}

/**
 * Rect plus effective radius of an element in layout-viewport px.
 * @param element
 */
export function measureHostRect( element: Element ): HostRect {
	const rect = element.getBoundingClientRect();
	const cs = window.getComputedStyle( element );
	return {
		...box( rect ),
		radius: clampRadius( cs.borderTopLeftRadius, rect.width, rect.height ),
	};
}

/**
 * The skin the morph surface starts in (prototype `openModal`/`glassOf`): the
 * computed background (a transparent origin gets the glass fallback), the
 * 1 px border or outline as an inset ring, and the backdrop blur.
 * @param element
 */
export function measureOriginSkin( element: Element ): HostOriginSkin {
	const cs = window.getComputedStyle( element );
	const background = isTransparent( cs.backgroundColor || '' )
		? TRANSPARENT_FALLBACK_BG
		: cs.backgroundColor;

	let ring = 'none';
	const borderWidth = Math.min( parseFloat( cs.borderTopWidth ) || 0, 1 );
	const outlineWidth = Math.min( parseFloat( cs.outlineWidth ) || 0, 1 );
	if (
		borderWidth > 0 &&
		cs.borderTopStyle !== 'none' &&
		! isTransparent( cs.borderTopColor || '' )
	) {
		ring = `inset 0 0 0 ${ borderWidth }px ${ cs.borderTopColor }`;
	} else if ( outlineWidth > 0 && cs.outlineStyle !== 'none' ) {
		ring = `inset 0 0 0 ${ outlineWidth }px ${ cs.outlineColor }`;
	}

	const filter = parseBackdropFilter(
		cs.backdropFilter ||
			( cs as unknown as { webkitBackdropFilter?: string } )
				.webkitBackdropFilter
	);
	return {
		background,
		ring,
		backdropBlurPx: filter.blurPx,
		...( filter.saturate !== undefined
			? { backdropSaturate: filter.saturate }
			: {} ),
	};
}

/**
 * Text and icon that fly into the panel (ghost handoff, 03 section 8).
 * @param element
 */
export function measureOriginSnapshot(
	element: Element
): HostOriginSnapshot | undefined {
	const snapshot: HostOriginSnapshot = {};
	const text = element.querySelector( TEXT_SELECTOR );
	if ( text ) {
		const rect = text.getBoundingClientRect();
		const cs = window.getComputedStyle( text );
		snapshot.text = {
			value: ( text.textContent || '' ).trim(),
			rect: box( rect ),
			fontSizePx: parseFloat( cs.fontSize ) || 16,
			color: cs.color,
			// The chat button never holds visitor input: its text lands as
			// the composer placeholder.
			isPlaceholder: true,
		};
	}
	const icon = element.querySelector( ICON_SELECTOR );
	if ( icon ) {
		const cs = window.getComputedStyle( icon );
		snapshot.icon = {
			kind:
				icon.getAttribute( 'data-dhsv-chat-morph-icon' ) === 'search'
					? 'search'
					: 'sparkle',
			rect: box( icon.getBoundingClientRect() ),
			color: cs.color,
		};
	}
	return snapshot.text || snapshot.icon ? snapshot : undefined;
}

export interface MorphOriginMeasure {
	originRect: HostRect;
	skin: HostOriginSkin;
	snapshot?: HostOriginSnapshot;
}

/**
 * Everything the iframe needs to start the surface pixel-identical. Must run
 * before any style change on the origin (anticipation, hiding). A zero-size
 * or detached origin yields `null`: open without morph.
 * @param element
 */
export function measureMorphOrigin(
	element: Element
): MorphOriginMeasure | null {
	if ( ! element.isConnected ) {
		return null;
	}
	const originRect = measureHostRect( element );
	if (
		! ( originRect.width > 0 && originRect.height > 0 ) ||
		! [ originRect.left, originRect.top ].every( Number.isFinite )
	) {
		return null;
	}
	const snapshot = measureOriginSnapshot( element );
	return {
		originRect,
		skin: measureOriginSkin( element ),
		...( snapshot ? { snapshot } : {} ),
	};
}

// ---------------------------------------------------------------------------
// Origin session: anticipation, hide, show + bounce, origin-rect updates
// ---------------------------------------------------------------------------

type Timer = ReturnType< typeof setTimeout >;

export interface MorphOriginSessionOptions {
	trigger: HTMLElement;
	flags: MorphMotionFlags;
	/** Send an `origin-rect` update. */
	sendRect: ( rect: HostRect ) => void;
	/**
	 * The origin is already compressed (a cold-start press handed over):
	 * hold it at the anticipation scale instead of animating there again.
	 */
	held?: boolean;
}

export interface MorphOriginSession {
	readonly trigger: HTMLElement;
	/** The host played the anticipation (`MorphMotionFlags.pressed`). */
	readonly pressed: boolean;
	readonly started: boolean;
	readonly landed: boolean;
	/** `morph-started`: the surface covers the origin, hide it. */
	start: () => void;
	/** `morph-landed`: show the origin and bounce. */
	land: () => void;
	/** The session is over: origin visible, listeners gone. Idempotent. */
	end: () => void;
}

type SavedStyle = { value: string; priority: string };

function saveStyle( el: HTMLElement, prop: string ): SavedStyle {
	return {
		value: el.style.getPropertyValue( prop ),
		priority: el.style.getPropertyPriority( prop ),
	};
}

function restoreStyle( el: HTMLElement, prop: string, saved: SavedStyle ) {
	if ( saved.value ) {
		el.style.setProperty( prop, saved.value, saved.priority );
	} else {
		el.style.removeProperty( prop );
	}
}

function canAnimate( el: Element ): boolean {
	return typeof ( el as HTMLElement ).animate === 'function';
}

function safeCancel( animation: Animation | null ): void {
	try {
		animation?.cancel();
	} catch {
		// already gone
	}
}

/**
 * Start the host half of a morph open. Call it right after measuring and in
 * the same task as the `open-request`.
 * @param options
 */
export function beginMorphOriginSession(
	options: MorphOriginSessionOptions
): MorphOriginSession {
	const { trigger, flags, sendRect, held = false } = options;
	const slow = flags.slow && flags.slow > 0 ? flags.slow : 1;
	let anticipation: Animation | null = null;
	let startTimer: Timer | null = null;
	let started = false;
	let landed = false;
	let ended = false;
	let hidden: { visibility: SavedStyle; transition: SavedStyle } | null =
		null;
	let stopTracking: ( () => void ) | null = null;

	if ( shouldAnticipate( flags ) && canAnimate( trigger ) ) {
		try {
			anticipation = trigger.animate(
				[
					{ scale: held ? String( ANTICIPATION_SCALE ) : '1' },
					{ scale: String( ANTICIPATION_SCALE ) },
				],
				{
					duration: ANTICIPATION_MS * slow,
					easing: ANTICIPATION_EASE,
					fill: 'forwards',
				}
			);
		} catch {
			anticipation = null;
		}
	}
	const pressed = anticipation !== null;

	const release = (): void => {
		if ( ! anticipation ) {
			return;
		}
		safeCancel( anticipation );
		anticipation = null;
		if ( canAnimate( trigger ) && ! flags.reducedMotion ) {
			try {
				trigger.animate(
					[ { scale: String( ANTICIPATION_SCALE ) }, { scale: '1' } ],
					{ duration: RELEASE_MS * slow, easing: RELEASE_EASE }
				);
			} catch {
				// cosmetic only
			}
		}
	};

	if ( pressed ) {
		startTimer = setTimeout( () => {
			startTimer = null;
			if ( ! started ) {
				release();
			}
		}, MORPH_START_TIMEOUT_MS * slow );
	}

	const hide = (): void => {
		if ( hidden ) {
			return;
		}
		hidden = {
			visibility: saveStyle( trigger, 'visibility' ),
			transition: saveStyle( trigger, 'transition' ),
		};
		// Same frame, no theme transition: a fading origin leaves a ghost
		// rectangle behind the surface (03 section 12).
		trigger.style.setProperty( 'transition', 'none', 'important' );
		trigger.style.setProperty( 'visibility', 'hidden', 'important' );
		trigger.classList.add( ORIGIN_OPEN_CLASS );
		suspendTyping( trigger, true );
	};

	const show = (): boolean => {
		if ( ! hidden ) {
			return false;
		}
		trigger.classList.remove( ORIGIN_OPEN_CLASS );
		restoreStyle( trigger, 'visibility', hidden.visibility );
		restoreStyle( trigger, 'transition', hidden.transition );
		hidden = null;
		suspendTyping( trigger, false );
		return true;
	};

	const clearStartTimer = (): void => {
		if ( startTimer ) {
			clearTimeout( startTimer );
			startTimer = null;
		}
	};

	return {
		trigger,
		pressed,
		get started() {
			return started;
		},
		get landed() {
			return landed;
		},
		start() {
			if ( started || ended ) {
				return;
			}
			started = true;
			clearStartTimer();
			hide();
			// Hidden first, then drop the held scale: no visible jump.
			safeCancel( anticipation );
			anticipation = null;
			stopTracking = trackOriginRect( trigger, sendRect );
		},
		land() {
			if ( landed || ended ) {
				return;
			}
			landed = true;
			clearStartTimer();
			stopTracking?.();
			stopTracking = null;
			safeCancel( anticipation );
			anticipation = null;
			const wasHidden = show();
			if ( wasHidden && ! flags.reducedMotion && canAnimate( trigger ) ) {
				try {
					trigger.animate( LANDING_BOUNCE_KEYFRAMES, {
						duration: LANDING_BOUNCE_MS * slow,
						easing: LANDING_BOUNCE_EASE,
					} );
				} catch {
					// cosmetic only
				}
			}
		},
		end() {
			if ( ended ) {
				return;
			}
			ended = true;
			clearStartTimer();
			stopTracking?.();
			stopTracking = null;
			if ( ! started ) {
				release();
			} else {
				safeCancel( anticipation );
				anticipation = null;
			}
			show();
		},
	};
}

/** What the host needs of a morph session to time the shrink after a close. */
export type MorphLandingSession = Pick<
	MorphOriginSession,
	'started' | 'landed' | 'start' | 'land' | 'end'
>;

/**
 * Host half of a morph the iframe starts from its own launcher (sticky bar,
 * its circle, the bubble). The launcher lives inside the iframe, which hides
 * and shows it itself; the host only tracks `morph-started` / `morph-landed`
 * so the iframe is shrunk once the surface has landed, not under it.
 */
export function beginLandingSession(): MorphLandingSession {
	let started = false;
	let landed = false;
	let ended = false;
	return {
		get started() {
			return started;
		},
		get landed() {
			return landed;
		},
		start() {
			if ( ! ended ) {
				started = true;
			}
		},
		land() {
			if ( ! ended ) {
				landed = true;
			}
		},
		end() {
			ended = true;
		},
	};
}

// ---------------------------------------------------------------------------
// Cold start: the trigger waits for the iframe (01 section 5)
// ---------------------------------------------------------------------------

export interface ColdStartPress {
	readonly trigger: HTMLElement;
	/** The anticipation plays and holds while the trigger waits. */
	readonly pressed: boolean;
	/**
	 * The morph takes over: drop the held scale without a release, in the
	 * same task as the origin is measured (unscaled) and the morph session
	 * starts held. Idempotent; returns whether the trigger was pressed.
	 */
	handOff: () => boolean;
	/** Nothing takes over (no morph, timeout, replaced): spring back. */
	release: () => void;
}

/**
 * A morph trigger was activated before the chat reported `chat-ready`: play
 * the anticipation now and hold it, and show a loading state after
 * `COLD_START_LOADING_DELAY_MS`. The open itself waits in the readiness gate
 * and morphs from the re-measured origin once the chat is ready.
 * @param trigger
 * @param flags
 */
export function beginColdStartPress(
	trigger: HTMLElement,
	flags: MorphMotionFlags
): ColdStartPress {
	const slow = flags.slow && flags.slow > 0 ? flags.slow : 1;
	let anticipation: Animation | null = null;
	let done = false;
	if ( shouldAnticipate( flags ) && canAnimate( trigger ) ) {
		try {
			anticipation = trigger.animate(
				[ { scale: '1' }, { scale: String( ANTICIPATION_SCALE ) } ],
				{
					duration: ANTICIPATION_MS * slow,
					easing: ANTICIPATION_EASE,
					fill: 'forwards',
				}
			);
		} catch {
			anticipation = null;
		}
	}
	const pressed = anticipation !== null;
	let loadingTimer: Timer | null = setTimeout( () => {
		loadingTimer = null;
		trigger.classList.add( LOADING_CLASS );
	}, COLD_START_LOADING_DELAY_MS );

	const finish = (): void => {
		done = true;
		if ( loadingTimer ) {
			clearTimeout( loadingTimer );
			loadingTimer = null;
		}
		trigger.classList.remove( LOADING_CLASS );
	};

	return {
		trigger,
		pressed,
		handOff() {
			if ( done ) {
				return false;
			}
			finish();
			const wasPressed = anticipation !== null;
			safeCancel( anticipation );
			anticipation = null;
			return wasPressed;
		},
		release() {
			if ( done ) {
				return;
			}
			finish();
			if ( ! anticipation ) {
				return;
			}
			safeCancel( anticipation );
			anticipation = null;
			if ( canAnimate( trigger ) && ! flags.reducedMotion ) {
				try {
					trigger.animate(
						[
							{ scale: String( ANTICIPATION_SCALE ) },
							{ scale: '1' },
						],
						{ duration: RELEASE_MS * slow, easing: RELEASE_EASE }
					);
				} catch {
					// cosmetic only
				}
			}
		},
	};
}

/**
 * Report the origin's rect while the chat is open (resize, orientation,
 * scrolling containers), at most once per frame and only when it changed.
 * @param trigger
 * @param sendRect
 * @return Stop function.
 */
export function trackOriginRect(
	trigger: Element,
	sendRect: ( rect: HostRect ) => void
): () => void {
	let raf: number | null = null;
	let last = '';
	const viewport = window.visualViewport;

	const measure = (): void => {
		raf = null;
		if ( ! trigger.isConnected ) {
			return;
		}
		const rect = measureHostRect( trigger );
		const key = [ rect.left, rect.top, rect.width, rect.height ]
			.map( ( n ) => Math.round( n * 2 ) / 2 )
			.join( ',' );
		if ( key === last ) {
			return;
		}
		last = key;
		sendRect( rect );
	};
	const schedule = (): void => {
		if ( raf === null ) {
			raf = window.requestAnimationFrame( measure );
		}
	};
	// The surface starts at the rect from the open-request.
	last = ( () => {
		const r = trigger.getBoundingClientRect();
		return [ r.left, r.top, r.width, r.height ]
			.map( ( n ) => Math.round( n * 2 ) / 2 )
			.join( ',' );
	} )();

	const opts: AddEventListenerOptions = { passive: true, capture: true };
	window.addEventListener( 'resize', schedule, opts );
	window.addEventListener( 'scroll', schedule, opts );
	viewport?.addEventListener( 'resize', schedule );
	viewport?.addEventListener( 'scroll', schedule );

	return () => {
		if ( raf !== null ) {
			window.cancelAnimationFrame( raf );
			raf = null;
		}
		window.removeEventListener( 'resize', schedule, opts );
		window.removeEventListener( 'scroll', schedule, opts );
		viewport?.removeEventListener( 'resize', schedule );
		viewport?.removeEventListener( 'scroll', schedule );
	};
}

// ---------------------------------------------------------------------------
// Typed example questions
// ---------------------------------------------------------------------------

type TypingEntry = {
	loop: TypingLoop;
	visible: boolean;
	suspended: boolean;
};

const typingEntries: WeakMap< Element, TypingEntry > = new WeakMap();

function syncTyping( entry: TypingEntry ): void {
	if ( entry.visible && ! entry.suspended && ! document.hidden ) {
		entry.loop.resume();
	} else {
		entry.loop.pause();
	}
}

/**
 * Pause the typing of an origin while the chat surface stands in for it.
 * @param element
 * @param suspended
 */
export function suspendTyping( element: Element, suspended: boolean ): void {
	const entry = typingEntries.get( element );
	if ( entry ) {
		entry.suspended = suspended;
		syncTyping( entry );
	}
}

/**
 * Start the typing engine on every morph trigger with example questions.
 * Static under `prefers-reduced-motion`; paused while the tab is hidden or the
 * element is off screen.
 * @param root Where to look for triggers.
 * @return Stop function (restores the idle labels).
 */
export function setupTypingEntries( root: ParentNode = document ): () => void {
	if ( matches( '(prefers-reduced-motion: reduce)' ) ) {
		return () => undefined;
	}
	const elements = Array.from(
		root.querySelectorAll(
			`[${ MORPH_ATTRIBUTE }][${ EXAMPLES_ATTRIBUTE }]`
		)
	);
	const started: Array< { el: Element; entry: TypingEntry } > = [];
	const clock = {
		now: () => window.performance.now(),
		setTimeout: ( run: () => void, ms: number ) =>
			window.setTimeout( run, ms ),
		clearTimeout: ( handle: unknown ) =>
			window.clearTimeout( handle as number ),
	};

	for ( const el of elements ) {
		if ( typingEntries.has( el ) ) {
			continue;
		}
		const examples = parseExamplesAttribute(
			el.getAttribute( EXAMPLES_ATTRIBUTE )
		);
		const text = el.querySelector( TEXT_SELECTOR );
		if ( ! examples.length || ! text ) {
			continue;
		}
		const idle = text.textContent || '';
		const loop = createTypingLoop( {
			idle,
			examples,
			clock,
			render: ( value, typing ) => {
				text.textContent = value;
				text.classList.toggle( TYPING_CLASS, typing );
			},
		} );
		const entry: TypingEntry = {
			loop,
			visible: typeof IntersectionObserver === 'undefined',
			suspended: false,
		};
		typingEntries.set( el, entry );
		started.push( { el, entry } );
	}
	if ( ! started.length ) {
		return () => undefined;
	}

	let observer: IntersectionObserver | null = null;
	if ( typeof IntersectionObserver !== 'undefined' ) {
		observer = new IntersectionObserver( ( records ) => {
			for ( const record of records ) {
				const entry = typingEntries.get( record.target );
				if ( entry ) {
					entry.visible = record.isIntersecting;
					syncTyping( entry );
				}
			}
		} );
		started.forEach( ( { el } ) => observer?.observe( el ) );
	}
	const onVisibility = (): void =>
		started.forEach( ( { entry } ) => syncTyping( entry ) );
	document.addEventListener( 'visibilitychange', onVisibility );

	// Turning reduced motion on mid-session freezes the idle label.
	let motionQuery: MediaQueryList | null = null;
	const onMotionChange = ( event: MediaQueryListEvent ): void => {
		if ( event.matches ) {
			stop();
		}
	};
	try {
		motionQuery = window.matchMedia?.( '(prefers-reduced-motion: reduce)' );
		motionQuery?.addEventListener?.( 'change', onMotionChange );
	} catch {
		motionQuery = null;
	}

	started.forEach( ( { entry } ) => syncTyping( entry ) );

	function stop(): void {
		observer?.disconnect();
		document.removeEventListener( 'visibilitychange', onVisibility );
		motionQuery?.removeEventListener?.( 'change', onMotionChange );
		started.forEach( ( { el, entry } ) => {
			entry.loop.stop();
			typingEntries.delete( el );
		} );
	}
	return stop;
}
